/**
 * Scarecrow (`hush.scarecrow.v1`) — the deliberate home of the face the v1 Hush lantern accidentally had
 * (hideout plan 04). The head forks the v1 kit lantern's lopsided six-sided outline
 * (`src/shared/assets/hushStatic/lantern.ts`) and its two-over-one layout, re-skinned as a burlap sack whose
 * holes become stitched eyes and a stitched smile. Body: a timber post and cross-arm, a draped patched coat with
 * straw at the cuffs and neck, and a floppy hat. One ground piece, drawn in frame pixels (≈1.6 ghosts tall).
 *
 * Never uses the blue ribbon or the warm three-window light — those belong to the Hush clue.
 */
import { checkEnvelope, checkScope, el, mix, n, partGroup, poly, rngFor, type HideoutPieceArt, type PieceRecipe, type Point, type ResolvedPalette } from '../pieces/svg';

export type ScarecrowVariant = 'field' | 'moor' | 'haunted';
export type ScarecrowRecipe = PieceRecipe<'hush.scarecrow.v1', { variant: ScarecrowVariant; tilt: number; wear: number }>;

export const SCARECROW_FRAME = { w: 96, h: 128 } as const;

/** v1 lantern outline (design units, bottom-center origin) — the forked head silhouette. */
const V1_OUTLINE: readonly Point[] = [
  { x: -30, y: -84 }, { x: 19, y: -90 }, { x: 36, y: -63 }, { x: 26, y: -10 }, { x: -21, y: -5 }, { x: -37, y: -43 },
];
/** v1 two-over-one centers; here they are the eyes and the mouth. */
const V1_MOTIF: readonly Point[] = [{ x: -12, y: -56 }, { x: 12, y: -56 }, { x: 0, y: -32 }];

export function validateScarecrowRecipe(input: unknown): ScarecrowRecipe {
  checkEnvelope(input, 'hush.scarecrow.v1');
  const r = input as ScarecrowRecipe;
  if (!['field', 'moor', 'haunted'].includes(r.params.variant)) throw new Error('hush.scarecrow.v1: variant');
  if (typeof r.params.tilt !== 'number' || !(r.params.tilt >= -1 && r.params.tilt <= 1)) throw new Error('hush.scarecrow.v1: tilt must be -1..1');
  if (typeof r.params.wear !== 'number' || !(r.params.wear >= 0 && r.params.wear <= 1)) throw new Error('hush.scarecrow.v1: wear must be 0..1');
  return r;
}

export function buildScarecrow(input: ScarecrowRecipe, roles: ResolvedPalette, scope: string): HideoutPieceArt {
  const recipe = validateScarecrowRecipe(input);
  checkScope(scope);
  const { variant, tilt, wear } = recipe.params;
  const rng = rngFor(recipe);
  const parts: string[] = [];
  const { w, h } = SCARECROW_FRAME;
  const cx = w / 2;
  const dim = variant === 'haunted' ? 0.45 : variant === 'moor' ? 0.2 : 0;
  const shade = (c: string) => mix(c, roles.outline, dim);
  const straw = shade(variant === 'moor' ? mix(roles.reedLight, roles.stoneBase, 0.4) : mix(roles.paperBase, roles.reedLight, 0.3));
  const strawDark = shade(roles.paperShadow);
  const coat = shade(variant === 'field' ? mix(roles.woodBase, roles.moss, 0.45) : variant === 'moor' ? roles.stoneShadow : mix(roles.woodShadow, roles.outline, 0.3));
  const sack = shade(mix(roles.paperBase, roles.woodBase, 0.45));
  const timber = shade(roles.woodShadow);
  const armY = 62;

  // Post and cross-arm.
  let frame = el('rect', { x: cx - 2.6, y: 44, width: 5.2, height: h - 45, fill: timber, stroke: roles.outline, 'stroke-width': 1 });
  frame += el('rect', { x: 8, y: armY - 2.4, width: w - 16, height: 4.8, fill: timber, stroke: roles.outline, 'stroke-width': 1 });
  frame += el('path', { d: `M${n(cx - 9)} ${n(h - 1.4)}L${n(cx)} ${n(h - 7)}L${n(cx + 9)} ${n(h - 1.4)}`, stroke: timber, 'stroke-width': 2.4, fill: 'none' });
  const body = partGroup(parts, 'scarecrow-frame', frame);

  // Coat: shoulders on the arm, ragged hem, sleeves to the arm ends, straw at cuffs, a patch.
  const hem: Point[] = [];
  for (let i = 0; i <= 8; i++) hem.push({ x: cx + 22 - i * 5.5, y: 104 + (i % 2) * 5 + rng(`hem.${i}`) * 4 });
  let coatArt = el('path', { d: poly([{ x: cx - 24, y: armY - 3 }, { x: cx + 24, y: armY - 3 }, { x: cx + 22, y: 104 }, ...hem, { x: cx - 22, y: 104 }]),
    fill: coat, stroke: roles.outline, 'stroke-width': 1.2 });
  for (const side of [-1, 1]) {
    const x0 = cx + side * 22, x1 = cx + side * 37;
    coatArt += el('path', { d: poly([{ x: x0, y: armY - 4 }, { x: x1, y: armY - 3 }, { x: x1, y: armY + 6 }, { x: x0, y: armY + 10 }]), fill: coat, stroke: roles.outline, 'stroke-width': 1.1 });
    let cuff = '';
    for (let i = 0; i < 4; i++) cuff += `M${n(x1)} ${n(armY - 2 + i * 2.6)}l${n(side * (3 + rng(`cuff.${side}.${i}`) * 3))} ${n(-1 + i)}`;
    coatArt += el('path', { d: cuff, stroke: straw, 'stroke-width': 1.3 });
  }
  coatArt += el('path', { d: `M${n(cx)} ${n(armY)}V${n(102)}`, stroke: mix(coat, roles.outline, 0.4), 'stroke-width': 1 });
  coatArt += el('rect', { x: cx + 6, y: 80, width: 10, height: 9, fill: shade(roles.stoneBase), stroke: roles.outlineSoft, 'stroke-width': 0.8, transform: `rotate(-8 ${cx + 11} 84)` });
  if (wear > 0.3) coatArt += el('path', { d: `M${n(cx - 15)} 90l4 5l3 -4`, stroke: mix(coat, roles.outline, 0.5), 'stroke-width': 1, fill: 'none' });
  const coatGroup = partGroup(parts, 'scarecrow-coat', coatArt);

  // Head: the v1 lantern outline at half scale, re-skinned as a tied burlap sack.
  const s = 0.42, hc = { x: cx, y: 54 };
  const head = V1_OUTLINE.map(p => ({ x: hc.x + p.x * s, y: hc.y + p.y * s }));
  let headArt = el('path', { d: poly(head), fill: sack, stroke: roles.outline, 'stroke-width': 1.3 });
  for (let i = 0; i < 6; i++) {
    const y = hc.y - 34 + i * 5.5;
    headArt += el('path', { d: `M${n(hc.x - 13)} ${n(y)}H${n(hc.x + 13)}`, stroke: mix(sack, roles.woodShadow, 0.4), 'stroke-width': 0.5, opacity: 0.6 });
  }
  const [le, re, mouth] = V1_MOTIF.map(p => ({ x: hc.x + p.x * s, y: hc.y + p.y * s }));
  const stitch = mix(roles.outline, roles.woodShadow, 0.3);
  for (const e of [le, re]) headArt += el('path', { d: `M${n(e.x - 2.4)} ${n(e.y - 2.4)}l4.8 4.8M${n(e.x + 2.4)} ${n(e.y - 2.4)}l-4.8 4.8`, stroke: stitch, 'stroke-width': 1.3 });
  headArt += el('path', { d: `M${n(mouth.x - 6)} ${n(mouth.y - 1)}Q${n(mouth.x)} ${n(mouth.y + 4)} ${n(mouth.x + 6)} ${n(mouth.y - 1)}`, stroke: stitch, 'stroke-width': 1.2, fill: 'none' });
  for (let i = -2; i <= 2; i++) headArt += el('path', { d: `M${n(mouth.x + i * 2.6)} ${n(mouth.y + 0.2 + (2 - Math.abs(i)) * 0.6)}v-2.2`, stroke: stitch, 'stroke-width': 0.7 });
  // Neck cord and straw tufts.
  headArt += el('path', { d: `M${n(cx - 8)} ${n(hc.y - 2)}Q${n(cx)} ${n(hc.y + 1.5)} ${n(cx + 8)} ${n(hc.y - 2)}`, stroke: strawDark, 'stroke-width': 1.6, fill: 'none' });
  let tuft = '';
  for (let i = 0; i < 6; i++) tuft += `M${n(cx - 6 + i * 2.4)} ${n(hc.y)}l${n(-2 + rng(`neck.${i}`) * 4)} ${n(4 + rng(`neck.l.${i}`) * 3)}`;
  headArt += el('path', { d: tuft, stroke: straw, 'stroke-width': 1.2 });

  // Hat: floppy brim and patched crown, tipped with the head.
  const hatColor = shade(variant === 'field' ? mix(roles.paperBase, roles.reedLight, 0.2) : roles.woodBase);
  let hat = el('path', { d: `M${n(hc.x - 22)} ${n(hc.y - 33)}Q${n(hc.x)} ${n(hc.y - 41)} ${n(hc.x + 22)} ${n(hc.y - 31)}Q${n(hc.x)} ${n(hc.y - 35)} ${n(hc.x - 22)} ${n(hc.y - 33)}Z`,
    fill: hatColor, stroke: roles.outline, 'stroke-width': 1.1 });
  hat += el('path', { d: `M${n(hc.x - 11)} ${n(hc.y - 36)}Q${n(hc.x - 9)} ${n(hc.y - 50)} ${n(hc.x + 1)} ${n(hc.y - 51)}Q${n(hc.x + 11)} ${n(hc.y - 49)} ${n(hc.x + 11)} ${n(hc.y - 35)}Z`,
    fill: hatColor, stroke: roles.outline, 'stroke-width': 1.1 });
  hat += el('path', { d: `M${n(hc.x - 11)} ${n(hc.y - 38)}Q${n(hc.x)} ${n(hc.y - 40)} ${n(hc.x + 11)} ${n(hc.y - 37)}`, stroke: shade(roles.woodShadow), 'stroke-width': 2, fill: 'none' });
  const angle = tilt * 9;
  const headGroup = partGroup(parts, 'scarecrow-head', headArt + partGroup(parts, 'scarecrow-hat', hat), { transform: `rotate(${n(angle)} ${n(cx)} ${n(hc.y)})` });

  return {
    frame: { w, h },
    body: body + coatGroup + headGroup,
    anchors: { support: { x: cx, y: h }, neck: { x: cx, y: hc.y }, hat_top: { x: hc.x, y: hc.y - 50 }, arm_left: { x: 10, y: armY }, arm_right: { x: w - 10, y: armY } },
    parts,
  };
}

const recipe = (variant: ScarecrowVariant, tilt: number, wear: number, seed: number): ScarecrowRecipe => ({
  schema: 'hush.asset-recipe.v1', id: `hush.scarecrow.${variant}.v1`, generatorId: 'hush.scarecrow.v1', seed, paletteId: 'coastal_salt',
  params: { variant, tilt, wear },
});

export const SCARECROW_RECIPES: Readonly<Record<ScarecrowVariant, ScarecrowRecipe>> = {
  field: recipe('field', 0.35, 0.3, 20067018),
  moor: recipe('moor', -0.5, 0.7, 20067019),
  haunted: recipe('haunted', 0.8, 0.9, 20067020),
};
